import { getPinCodeDisplayName } from './data';
import { REGION } from '../../../../data/idConstants';

function validatePincode(pincode, country) {
  let countryCode = '';
  if (country && typeof country === 'object' && country.id) {
    countryCode = country.id;
  } else if (typeof country === 'string') {
    countryCode = country;
  }
  const { pickupDynamicFields } = require('./data');

  let pincodeField = null;
  for (const section of pickupDynamicFields) {
    if (section.fields) {
      pincodeField = section.fields.find((field) => field.name === 'pincode');
      if (pincodeField) break;
    }
  }

  if (!pincodeField || !pincodeField.validation) {
    return '';
  }

  let validationRule = pincodeField.validation.rule;
  if (
    countryCode &&
    pincodeField.validation.countrySpecificValidation?.[countryCode]
  ) {
    const countryVal =
      pincodeField.validation.countrySpecificValidation[countryCode];
    validationRule = {
      ...validationRule,
      regex: { pattern: countryVal.regex },
      length: countryVal.length
    };
  }
  let pinError = '';
  if (validationRule.regex?.pattern) {
    if (!validationRule.regex.pattern.test(pincode)) {
      pinError = `Invalid ${getPinCodeDisplayName()} format for selected country`;
    }
  }
  if (validationRule.length) {
    if (pincode.length > validationRule.length.max) {
      pinError = `${getPinCodeDisplayName()} must be ${validationRule.length.max} characters`;
    }
  }
  return pinError;
}

export function handleInputChange(
  event,
  setPickupAddressDetails,
  setPickupAddressFormErrors
) {
  let name, value, error;
  if (event?.target) {
    name = event.target.name;
    value = event.target.value;
    error = event.target.error || '';
  } else {
    name = event.name;
    value = event.value;
    error = event.error || '';
  }

  if (name === 'pincode') {
    if (typeof value === 'string') {
      value = value.replace(/[a-z]/g, (char) => char.toUpperCase());
    }
    setPickupAddressDetails((prev) => {
      const pinError = validatePincode(value, prev.country);
      setPickupAddressFormErrors((prevErr) => ({ ...prevErr, [name]: pinError }));
      return { ...prev, [name]: value };
    });
    return;
  }
  if (name === 'isdCode') {
    const isdCodeValue =
      value && typeof value === 'object' ? value.value || value.displayValue : value;
    const fieldError = isdCodeValue && isdCodeValue.trim() !== '' ? '' : error;

    setPickupAddressDetails((prev) => ({ ...prev, [name]: isdCodeValue }));
    setPickupAddressFormErrors((prev) => ({ ...prev, [name]: fieldError }));
    return;
  }
  if (name === 'contactNo') {
    setPickupAddressDetails((prev) => ({ ...prev, [name]: value }));
    setPickupAddressFormErrors((prev) => ({ ...prev, [name]: error }));
    return;
  }
  if (name === 'country') {
    setPickupAddressDetails((prev) => {
      const updated = { ...prev, [name]: value };
      if (updated.pincode) {
        const pinError = validatePincode(updated.pincode, value);
        setPickupAddressFormErrors((prevErr) => ({
          ...prevErr,
          pincode: pinError,
          [name]: error
        }));
      } else {
        setPickupAddressFormErrors((prevErr) => ({ ...prevErr, [name]: error }));
      }
      return updated;
    });
    return;
  }
  setPickupAddressDetails((prev) => ({ ...prev, [name]: value }));
  setPickupAddressFormErrors((prev) => ({ ...prev, [name]: error }));
}

const removeDuplicateCountries = (countryList) => {
  return countryList.filter(
    (country, index, self) =>
      index ===
      self.findIndex(
        (item) =>
          item.countryCode === country.countryCode &&
          item.isdCode === country.isdCode
      )
  );
};

const groupCountriesByIsdCode = (countries) => {
  return countries.reduce((acc, country) => {
    const isdCode = country.isdCode;
    if (!acc[isdCode]) {
      acc[isdCode] = [];
    }
    acc[isdCode].push(country);
    return acc;
  }, {});
};

const createCountryOption = (isdCode, countries) => {
  if (countries.length === 1) {
    const country = countries[0];
    return {
      id: country.countryCode,
      label: `${country.isdCode} (${country.countryName})`,
      value: country.isdCode,
      displayValue: country.isdCode
    };
  }

  const sortedCountries = countries.sort((a, b) =>
    b.countryName.localeCompare(a.countryName)
  );
  const countryNames = sortedCountries.map((item) => item.countryName).join('/');
  const primaryCountry = sortedCountries[0];
  return {
    id: primaryCountry.countryCode,
    label: `${isdCode} (${countryNames})`,
    value: isdCode,
    displayValue: isdCode
  };
};

export const createCountryOptions = (countryList) => {
  const uniqueCountries = removeDuplicateCountries(countryList);
  const groupedByIsdCode = groupCountriesByIsdCode(uniqueCountries);

  const options = Object.keys(groupedByIsdCode).map((isdCode) =>
    createCountryOption(isdCode, groupedByIsdCode[isdCode])
  );

  return options;
};

export const getDefaultIsdCodeByRegion = (countryList = []) => {
  const currentRegion = process.env.REACT_APP_REGION;

  const regionCountryMap = {
    [REGION.IN.CODE]: 'IN',
    [REGION.EU.CODE]: 'DE',
    [REGION.NA.CODE]: 'US'
  };

  const fallbackMap = {
    [REGION.IN.CODE]: { isdCode: '+91', countryCode: 'IN' },
    [REGION.EU.CODE]: { isdCode: '+49', countryCode: 'DE' },
    [REGION.NA.CODE]: { isdCode: '+1', countryCode: 'US' }
  };

  const targetCountryCode = regionCountryMap[currentRegion] || 'IN';
  const country = countryList.find((item) => item.countryCode === targetCountryCode);

  return country
    ? { isdCode: country.isdCode, countryCode: country.countryCode }
    : fallbackMap[currentRegion] || fallbackMap[REGION.IN.CODE];
};

export const pickupSectionForm = (
  pickupDynamicFields,
  pickupAddressDetails,
  pickupAddressFormErrors,
  countryList,
  regionSpecificCountryList,
  getCountryOptions
) => {
  return pickupDynamicFields.map((section) => ({
    ...section,
    fields: section.fields.map((field) => {
      if (field.type === 'COMBINE' && Array.isArray(field.attributes)) {
        return {
          ...field,
          attributes: field.attributes.map((subField) => {
            if (subField.name === 'isdCode') {
              const isdCodeOptions = createCountryOptions(countryList || []);

              return {
                ...subField,
                value: pickupAddressDetails[subField.name] || '',
                error: pickupAddressFormErrors[subField.name] || '',
                options: isdCodeOptions
              };
            }
            return {
              ...subField,
              value: pickupAddressDetails[subField.name] || '',
              error: pickupAddressFormErrors[subField.name] || '',
              options: subField.options || []
            };
          })
        };
      }
      return {
        ...field,
        value: pickupAddressDetails[field.name] || '',
        error: pickupAddressFormErrors[field.name] || '',
        disabled:
          field.name === 'country' &&
          process.env.REACT_APP_REGION === 'IN' &&
          pickupAddressDetails.country,
        options:
          field.name === 'country'
            ? getCountryOptions(regionSpecificCountryList, 'countryName')
            : field.options || []
      };
    })
  }));
};

export const DialogButtons = (content, onClose, handleSave, isFormValid) => {
  return [
    {
      label: content?.constants?.cancel,
      onClick: onClose,
      mode: 'secondary'
    },
    {
      label: content?.constants?.save,
      onClick: handleSave,
      mode: 'primary',
      disabled: !isFormValid
    }
  ];
};
